/**
 * Time-in-lieu claims on Approvals (DESIGN-toil.md screen 4), above the leave requests.
 * Only the claims the server says this person may sign off (`GET /toil/pending`, which asks
 * `services/toil.may_sign_off`). The company's usual rate is already ticked, so most are one
 * click (E1); "Enter it" changes the amount (B); Decline takes an optional note.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { toilApi } from '../../lib/api';
import { formatDateGB } from '../../lib/dates';
import { fmtToil, givenMinutes, toMinutes } from '../../lib/toil';
import RatePicker from './RatePicker';

function Claim({ c, usual, onDone }) {
  const [rate, setRate] = useState(usual || '1');
  const [customH, setCustomH] = useState('');
  const [customM, setCustomM] = useState('');
  const [declining, setDeclining] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const custom = toMinutes(customH, customM);
  const given = givenMinutes(c.worked_minutes, rate, custom);

  const send = async (approve) => {
    if (approve && !given) { toast.error('Enter the time to give back'); return; }
    setBusy(true);
    try {
      await toilApi.decide(c.id, approve
        ? { approve: true, rate, custom_minutes: rate === 'custom' ? custom : null }
        : { approve: false, note: note.trim() || null });
      toast.success(approve ? 'Time in lieu approved' : 'Claim declined');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save that');
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-border p-4 grid gap-4 md:grid-cols-[1.1fr_2fr]" data-testid="toil-pending-claim">
      <div>
        <p className="font-semibold">{c.name}</p>
        <p className="text-sm text-muted-foreground">Worked {formatDateGB(c.worked_on)} · <b className="text-foreground">{c.worked}</b> extra</p>
        {c.note && <p className="text-sm mt-1">“{c.note}”</p>}
        <p className="text-xs text-muted-foreground mt-1">Time in lieu now: {c.to_use}</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-muted-foreground mb-1.5">Time to give back</p>
        <RatePicker worked={c.worked_minutes} rate={rate} onRate={setRate} usual={usual}
          customH={customH} customM={customM} onCustom={(h, m) => { setCustomH(h); setCustomM(m); }} />
        {declining && (
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
            placeholder="A note for them (optional)" data-testid="toil-decline-note"
            className="input-hive mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
        )}
        <div className="flex justify-end gap-2 mt-3">
          {declining ? (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setDeclining(false)}>Back</Button>
              <Button type="button" size="sm" className="bg-red-600 hover:bg-red-700 text-white" disabled={busy}
                onClick={() => send(false)} data-testid="toil-decline-confirm">Decline</Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" size="sm" onClick={() => setDeclining(true)} data-testid="toil-decline">Decline…</Button>
              <Button type="button" size="sm" className="btn-primary" disabled={busy || !given}
                onClick={() => send(true)} data-testid="toil-approve">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (given ? `Approve ${fmtToil(given)}` : 'Approve')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ToilClaimsQueue({ onCount }) {
  const [data, setData] = useState(null);
  const load = useCallback(() => {
    toilApi.pending().then((r) => {
      setData(r.data);
      if (onCount) onCount((r.data?.claims || []).length);
      window.dispatchEvent(new Event('toil:pending-changed'));
    }).catch(() => { setData(null); if (onCount) onCount(0); });
  }, [onCount]);
  useEffect(() => { load(); }, [load]);

  const claims = data?.claims || [];
  if (claims.length === 0) return null;
  return (
    <div className="card-hive p-6" data-testid="toil-claims">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
          <Clock className="w-5 h-5 text-teal-500" /> Time in lieu claims
        </h2>
        <span className="text-sm text-muted-foreground">{claims.length} waiting</span>
      </div>
      <div className="space-y-3">
        {claims.map((c) => <Claim key={c.id} c={c} usual={data.default_rate} onDone={load} />)}
      </div>
    </div>
  );
}
